/**
 * Generic read-cache utilities built on top of Dexie.
 *
 * Typical usage:
 *   const leads = await readCache('leads', () =>
 *     api.get('/leads/my-queue').then(r => r.data.items as Lead[])
 *   )
 *
 * Fresh rows (within TTL) are returned from Dexie without hitting the network.
 * Stale/empty cache triggers the fetcher, repopulates Dexie, then returns rows.
 * Network failure falls back to stale cache rather than throwing.
 */

import { offlineDB, isFresh } from './db'
import { OFFLINE_CONFIG } from './offlineConfig'
import type { Lead, CallLog } from '../types'
import type { Customer } from '../types'

export type EntityName = 'leads' | 'customers' | 'callHistory'
export type EntityRow = Lead | Customer | CallLog

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function tableFor(entity: EntityName): any {
  if (entity === 'leads') return offlineDB.leads
  if (entity === 'customers') return offlineDB.customers
  return offlineDB.callHistory
}

/**
 * Read from cache if fresh, otherwise fetch and repopulate.
 *
 * @param entity   Cache bucket name
 * @param fetcher  Returns fresh data from the API
 * @param filter   Optional — narrows which cached rows to return.
 *                 Receives the raw Dexie table; must return a Promise<T[]>.
 */
export async function readCache<T extends EntityRow>(
  entity: EntityName,
  fetcher: () => Promise<T[]>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: (table: any) => Promise<T[]>,
): Promise<T[]> {
  const cfg = OFFLINE_CONFIG[entity]
  const table = tableFor(entity)

  const sample = await table.orderBy('_cachedAt').last()
  const fresh = sample ? isFresh(sample._cachedAt, cfg.ttlMs) : false

  if (fresh) {
    return filter ? filter(table) : table.toArray()
  }

  let items: T[]
  try {
    items = await fetcher()
  } catch {
    // Network unavailable — return stale cache
    return filter ? filter(table) : table.toArray()
  }

  // Non-critical — cache write failures (e.g. stale null-key records in the
  // eviction path) must not prevent the caller from receiving live data.
  await populateCache(entity, items).catch(() => {})
  return filter ? filter(table) : table.toArray()
}

/**
 * Write rows into the cache with a fresh _cachedAt timestamp.
 * Evicts oldest rows when maxRows is exceeded.
 */
export async function populateCache(entity: EntityName, items: EntityRow[]): Promise<void> {
  if (!Array.isArray(items)) return
  const cfg = OFFLINE_CONFIG[entity]
  const now = new Date().toISOString()
  const table = tableFor(entity)

  // Filter out items with null/undefined primary key — Dexie rejects them with
  // "Invalid key provided" if the schema primary key field is missing.
  const valid = items.filter(item => item.id != null)
  await table.bulkPut(valid.map(item => ({ ...item, _cachedAt: now })))

  const count = await table.count()
  if (count > cfg.maxRows) {
    const overflow = count - cfg.maxRows
    const oldest: unknown[] = await table.orderBy('_cachedAt').limit(overflow).primaryKeys()
    const validKeys = oldest.filter(k => k != null)
    if (validKeys.length > 0) await table.bulkDelete(validKeys)
  }
}

/** Upsert a single entity (optimistic update after a write). */
export async function upsertCached(entity: EntityName, item: EntityRow): Promise<void> {
  const now = new Date().toISOString()
  await tableFor(entity).put({ ...item, _cachedAt: now })
}

/** Remove a single entity by primary key. */
export async function removeCached(entity: EntityName, id: number): Promise<void> {
  await tableFor(entity).delete(id)
}
